import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Role } from "../../../models/enums";
import { AuthService } from "../../auth/auth.service";

/**
 * Guard d'abonnement : l'organisation doit avoir un abonnement valide.
 *
 * Synchrone : le profil (et donc l'organisation + ses abonnements) est chargé avant
 * la première navigation par `provideSessionBootstrap` (APP_INITIALIZER).
 *
 * Redirections :
 *  - pas de profil → /sign-in, avec l'URL demandée en `queryParams.returnUrl` pour
 *    que `AuthService.setupRoles` y renvoie l'utilisateur une fois connecté ;
 *  - gérants de l'abonnement (SUPER_ADMIN) → /admin/subscription pour régulariser ;
 *  - autres utilisateurs → page d'information correspondant au statut.
 */
export const SubscriptionActiveGuard = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree => {
  const authService = inject(AuthService)
  const router = inject(Router)

  if (!authService.profile) {
    const queryParams = state.url && state.url !== '/' ? { returnUrl: state.url } : undefined;
    return router.createUrlTree(['/sign-in'], queryParams ? { queryParams } : undefined);
  }

  const status = authService.getSubscriptionStatus();
  if (status === 'active') {
    return true
  }

  if (authService.hasRoleOrSuperAdmin([Role.SuperAdmin])) {
    return router.createUrlTree(['/admin/subscription'])
  }

  switch (status) {
    case 'trial_expired':
      return router.createUrlTree(['/subscriptions/trial-ended'])
    case 'suspended':
      return router.createUrlTree(['/subscriptions/suspended'])
    case 'not_found':
    default:
      return router.createUrlTree(['/subscriptions/not-found'])
  }
}
